import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import {
  getFieldSections,
  getFieldSets,
  getFieldSetById,
} from '@/api/asset-type';
import type { AssetTypeResolvedField } from '@gokrew/models/asset-type';
import { useAssetTypeById } from './useAssetTypes';
import { QueryKeys } from '@/constants/query-keys';
import { useTranslation } from 'react-i18next';

interface UseAssetTypeFieldsReturn {
  assetFields: AssetTypeResolvedField[];
  variationFields: AssetTypeResolvedField[];
}

export function useFieldSections() {
  const { companyId } = useAuth();
  const {
    i18n: { language },
  } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.FieldSections, companyId, language],
    queryFn: async () => {
      const res = await getFieldSections(companyId!, language);
      return res.data;
    },
    enabled: !!companyId,
  });
}

export function useFieldSets() {
  const { companyId } = useAuth();
  const {
    i18n: { language },
  } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.FieldSets, companyId, language],
    queryFn: async () => {
      const res = await getFieldSets(companyId!, language);
      return res.data;
    },
    enabled: !!companyId,
  });
}

export function useFieldSetById(id?: string) {
  const { companyId } = useAuth();
  const {
    i18n: { language },
  } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.FieldSet, id, language],
    queryFn: async () => {
      const res = await getFieldSetById(companyId!, id!, language);
      return res.data;
    },
    enabled: !!companyId && !!id,
  });
}

export function useAssetTypeFields(
  assetTypeId?: string
): UseAssetTypeFieldsReturn {
  const { data } = useAssetTypeById(assetTypeId ?? '');

  const fields = data?.fields ?? [];

  const assetFields = useMemo(
    () => fields.filter((f: AssetTypeResolvedField) => f.scope === 'model'),
    [fields]
  );

  const variationFields = useMemo(
    () => fields.filter((f: AssetTypeResolvedField) => f.scope === 'unit'),
    [fields]
  );

  return {
    assetFields,
    variationFields,
  };
}
